<template>
  <fullscreen-dialog
    v-model="model"
    :title="user ? toLatin(user.user_name) : ''"
    :subtitle="`ID: ${user ? user.user_id : ''}`"
    hide-footer
  >
    <template #default>
      <v-card-text>
        <div v-if="!user">{{ $t("no_data") }}</div>
        <div v-else>
          <!-- Summary -->
          <v-card outlined class="mb-4">
            <v-card-text>
              <v-row dense justify="space-between">
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">{{ $t("orders") }}</div>
                  <div class="text-h6 primary--text">{{ filteredRows.length }}</div>
                </v-col>
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">{{ $t("orders_total") }}</div>
                  <div class="text-h6">{{ filteredOrdersTotal.toFixed(2) }}</div>
                </v-col>
                <v-col cols="6" sm="auto">
                  <div class="text-subtitle-2 grey--text mb-1">{{ $t("spent_on_credits") }}</div>
                  <div class="text-h6 error--text">{{ Math.abs(filteredCreditsSpentRaw).toFixed(2) }}</div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Filters -->
          <v-row align="center" class="mb-2">
            <v-col cols="auto">
              <month-picker v-model="selectedMonth" color="white" />
            </v-col>
            <v-col cols="3" md="2">
              <text-field v-model="search" kind="search" :label="$t('search')" clearable color="white" />
            </v-col>
            <v-col align="end">
              <v-row no-gutters align="center" justify="end">
                <excel-button class="mr-2" @click="exportToExcel" />
                <print-button @click="printReport" />
              </v-row>
            </v-col>
          </v-row>

          <!-- Table -->
          <v-data-table
            :headers="headers"
            :items="filteredRowsWithTotal"
            hide-default-footer
            disable-pagination
            class="striped-table ordera-box-shadow"
            :loading="loading"
          >
            <template v-slot:item.num="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">{{ item.num }}</span>
            </template>

            <template v-slot:item.date="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold' : ''">
                {{ item.isTotal ? $t("total").toUpperCase() : localeDate(item.date) }}
              </span>
            </template>

            <template v-slot:item.restaurant="{ item }">
              <span v-if="!item.isTotal">{{ toLatin(item.restaurant) }}</span>
            </template>

            <template v-slot:item.meal_name="{ item }">
              <span v-if="!item.isTotal">{{ toLatin(item.meal_name) }}</span>
            </template>

            <template v-slot:item.total="{ item }">
              <span :class="item.isTotal ? 'font-weight-bold primary--text' : ''">
                {{ item.total.toFixed(2) }}
              </span>
            </template>

            <template v-slot:item.credits_spent="{ item }">
              <span v-if="!item.isTotal" :class="item.credits_spent < 0 ? 'error--text' : ''">
                {{ item.credits_spent !== 0 ? item.credits_spent.toFixed(2) : "" }}
              </span>
              <span v-else class="font-weight-bold error--text">
                {{ filteredCreditsSpentRaw.toFixed(2) }}
              </span>
            </template>
          </v-data-table>
        </div>
      </v-card-text>
    </template>
  </fullscreen-dialog>
</template>

<script>
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import reportService from "@/services/report";
import hasModel from "@/mixins/hasModel";
import hasExcel from "@/mixins/hasExcel";
import hasPrinting from "@/mixins/hasPrinting";

export default {
  components: { FullscreenDialog },
  mixins: [hasModel, hasExcel, hasPrinting],
  props: {
    user: { type: Object, default: null },
  },
  data() {
    const now = new Date();
    return {
      selectedMonth: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
      search: "",
      reportData: null,
      loading: false,
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false, width: "80px" },
        { text: this.$t("date"), value: "date", sortable: true },
        { text: this.$t("restaurant"), value: "restaurant", sortable: false },
        { text: this.$t("meal"), value: "meal_name", sortable: false },
        { text: this.$t("amount"), value: "total", sortable: false, align: "right" },
        { text: this.$t("spent_on_credits"), value: "credits_spent", sortable: false, align: "right" },
      ];
    },
    monthName() {
      if (!this.selectedMonth) return "";
      return this.localeDate(this.selectedMonth + "-01", { month: "long", year: "numeric" });
    },
    allRows() {
      const orders = this.reportData?.orders || [];
      return orders
        .filter((o) => o && o.id)
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map((o, i) => ({
          id: o.id,
          date: o.date,
          restaurant: o.restaurant,
          meal_name: (o.items || []).map((item) => item.name).join(", "),
          total: (o.total_cents || 0) / 100,
          credits_spent: o.credits_spent || 0,
          isTotal: false,
          num: i + 1,
        }));
    },
    filteredRows() {
      let rows = this.allRows;
      if (this.search) {
        const q = this.search.toLowerCase();
        rows = rows.filter(
          (r) =>
            (r.meal_name || "").toLowerCase().includes(q) ||
            (r.restaurant || "").toLowerCase().includes(q) ||
            this.localeDate(r.date).toLowerCase().includes(q)
        );
      }
      return rows.map((r, i) => ({ ...r, num: i + 1 }));
    },
    filteredRowsWithTotal() {
      const rows = [...this.filteredRows];
      if (rows.length > 0) {
        rows.push({
          id: "total",
          num: "",
          date: "",
          restaurant: "",
          meal_name: "",
          total: this.filteredOrdersTotal,
          credits_spent: this.filteredCreditsSpentRaw,
          isTotal: true,
        });
      }
      return rows;
    },
    filteredOrdersTotal() {
      return this.filteredRows.reduce((sum, r) => sum + r.total, 0);
    },
    filteredCreditsSpentRaw() {
      return this.filteredRows.reduce((sum, r) => sum + r.credits_spent, 0);
    },
  },
  watch: {
    selectedMonth() {
      this.fetchReport();
    },
    user(val) {
      this.search = "";
      if (val) this.fetchReport();
    },
    model(val) {
      if (val) {
        this.search = "";
        this.fetchReport();
      }
    },
  },
  methods: {
    monthDateRange(yearMonth) {
      const [year, month] = yearMonth.split("-").map(Number);
      const date_from = `${year}-${String(month).padStart(2, "0")}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const date_to = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
      return { date_from, date_to };
    },
    async fetchReport() {
      if (!this.user || !this.selectedMonth) return;
      const sc = this.$store.state.report.selectedMealCompany;
      const companyId = sc && typeof sc === "object" ? sc.id : sc;
      if (!companyId) return;
      const { date_from, date_to } = this.monthDateRange(this.selectedMonth);
      this.loading = true;
      this.reportData = null;
      try {
        const { data } = await reportService.getCreditUser({
          date_from,
          date_to,
          company_id: companyId,
          user_id: this.user.user_id,
        });
        if (Array.isArray(data)) {
          this.reportData = data.find((u) => u.user_id === this.user.user_id) || data[0] || null;
        } else if (data?.users) {
          this.reportData = data.users.find((u) => u.user_id === this.user.user_id) || data.users[0] || null;
        } else {
          this.reportData = data;
        }
      } catch {
        this.reportData = null;
      } finally {
        this.loading = false;
      }
    },
    exportToExcel() {
      const items = this.filteredRows.map((r) => ({
        num: r.num,
        date: this.localeDate(r.date),
        restaurant: this.toLatin(r.restaurant),
        meal_name: this.toLatin(r.meal_name),
        total: r.total.toFixed(2),
        credits_spent: r.credits_spent !== 0 ? r.credits_spent.toFixed(2) : "",
      }));
      items.push({
        num: "",
        date: this.$t("total"),
        restaurant: "",
        meal_name: "",
        total: this.filteredOrdersTotal.toFixed(2),
        credits_spent: this.filteredCreditsSpentRaw.toFixed(2),
      });
      this.downloadExcel({
        fileName: `${this.toLatin(this.user.user_name)}_${this.monthName}.xlsx`,
        sheets: [
          {
            name: this.toLatin(this.user.user_name),
            headers: this.headers,
            items,
            title: this.toLatin(this.user.user_name),
            subtitle: this.monthName,
          },
        ],
      });
    },
    printReport() {
      const items = this.filteredRows.map((r) => ({
        num: r.num,
        date: this.localeDate(r.date),
        restaurant: this.toLatin(r.restaurant),
        meal_name: this.toLatin(r.meal_name),
        total: r.total.toFixed(2),
        credits_spent: r.credits_spent !== 0 ? r.credits_spent.toFixed(2) : "",
      }));
      items.push({
        num: "",
        date: this.$t("total"),
        restaurant: "",
        meal_name: "",
        total: this.filteredOrdersTotal.toFixed(2),
        credits_spent: this.filteredCreditsSpentRaw.toFixed(2),
      });
      this.printDocument({
        title: this.toLatin(this.user.user_name),
        subtitle: this.monthName,
        tables: [{ headers: this.headers, items }],
      });
    },
  },
};
</script>
